import React, { Component } from "react";
import {
  cleanName,
  prepareSpec,
  replaceParameters,
  addEndpointToPaths,
  getEnvironmentName,
} from "../../utils/spec-utils";
import ApiEndpoint from "./apiEndpoint";
import { AttributeSelection } from "./AttributeSelection";
import { API_VERSION_STATUS, API_ENVIRONMENTS } from "./apiEndpoint/constants";
import { subscriptionId, authorization } from "../../config/defaultModels";
import {
  IconFileDownload,
  TextButton,
  StatusIndicator,
  STATUS_INDICATOR_VARIANTS,
} from "cdk-radial";
import { APIDetailsHeader } from "../common/ApiDetailsTabHeader";
import {
  Endpoints,
  EndpointsList,
  EndpointDetails,
  EndpointListItem,
  EndpointListInfo,
  EndpointListName,
  EndpointListMethod,
  ApiHeaderDetails,
  ApiVersionSection,
  RateInput,
  VersionSeperator
} from "./styledComponents";
import { Fragment } from "react";
import { TestSimulator } from "./apiEndpoint/TestSimulator";
import { CopyToClipboard } from "../common/CopyToClipboard";

const parameterReplacements = [subscriptionId, authorization];
const endpointReplacements = [];

const ReleaseTag = ({ status }) => {
  const STATUS_COLOR = {
    [API_VERSION_STATUS.BETA]: STATUS_INDICATOR_VARIANTS.WARNING,
    [API_VERSION_STATUS.GENERAL_RELEASE]: STATUS_INDICATOR_VARIANTS.POSITIVE,
    [API_VERSION_STATUS.DEPRECATED]: STATUS_INDICATOR_VARIANTS.INFORMATION,
  };
  return (
    <StatusIndicator
      data-testid={`${status}-release-tag`}
      label={<div style={{ fontSize: "15px" }}>{status}</div>}
      textColor="#0000"
      variant={STATUS_COLOR[status] || STATUS_INDICATOR_VARIANTS.INFORMATION}
    />
  );
};

const noVersionsText = (auth) => {
  return auth?.loginPending ? (
    ""
  ) : (
    <div className="api-dir-pkg_no-documentation-text api-dir-pkg_api-spec-text">
      Public versions are not available.
      {"\n"}
      <a
        className="api-dir-pkg_cursor-pointer"
        onClick={() => {
          auth.login();
        }}
      >
        Sign in
      </a>{" "}
      to view more information or contact API provider.
    </div>
  );
};

const SpecDownload = ({ downloadSpec, content }) => {
  return (
    <TextButton
      className="api-dir-pkg_download_spec_btn"
      data-cy="download_spec_btn"
      id="downloadSpec"
      onClick={() => {
        downloadSpec(content);
      }}
      icon={<IconFileDownload />}
      text="Download API Spec"
    />
  );
};

class ApiExplorer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      endpoint: null,
      content: null,
      showToaster: false,
      selectedEnvironment: null,
      selectedVersion: null,
      lifeCycleStatus: null,
      env: [],
      specToDisplay: null,
      environment: [],
      environments: {},
      versions: [],
      version: [],
      proxyUrl: null,
      namespace: null,
      advancedProxySettings: {},
      dataRefreshRate: null
    };
  }

  componentDidMount = async () => {
    const apiData = { ...this.props.api };
    if (!this.props.auth.isAuthenticated) {
      const prodInstance = apiData?.instances?.find(
        (instance) =>
          instance.environment === "prod" &&
          (instance.lifecycleStatus === API_VERSION_STATUS.GENERAL_RELEASE ||
            instance.lifecycleStatus === null)
      );

      const specContent = prodInstance
        ? await prepareSpec(prodInstance.spec)
        : {};

      this.setState({
        selectedEnvironment: "Production",
        selectedVersion: prodInstance ? prodInstance.version : "",
        content: specContent,
        namespace: prodInstance ? prodInstance?.config?.namespace : "",
        proxyUrl: prodInstance ? prodInstance.config?.proxyUrl : "",
        dataRefreshRate: apiData?.dataRefreshRate,
        advancedProxySettings: prodInstance ? prodInstance.config.advancedProxySettings : {},
      });
    } else {
      this.generateEnvironmentMap();
    }
  };

  componentDidUpdate = async (prevProps) => {
    if (!prevProps.auth.isAuthenticated && this.props.auth.isAuthenticated) {
      this.generateEnvironmentMap();
    }
  };

  setEndpoint = (endpoint) => {
    this.setState({ endpoint });
  };

  generateEnvironmentMap = () => {
    let apiData = {};
    Object.assign(apiData, JSON.parse(JSON.stringify(this.props.api)));
    apiData.environments?.forEach((environment) => {
      if (environment.name === API_ENVIRONMENTS.Production) {
        environment.name = "Production";
      }
      if (environment.name === API_ENVIRONMENTS.Test) {
        environment.name = "Test";
      }
      let version = [];
      environment.instances.forEach((inst) => {
        version.push({
          [inst.version]: {
            lifecycleStatus: inst.lifecycleStatus,
            spec: inst.spec,
            proxy: inst.config.proxyUrl,
            namespace: inst.config.namespace,
          },
        });
      });
      this.state.env.push({ [environment.name]: version });
    });

    this.setState(
      {
        environments: apiData.environments?.reduce(
          (acc, env) => ({ ...acc, [env.name]: env }),
          {}
        ),
        selectedEnvironment: getEnvironmentName(apiData.environments),
        dataRefreshRate: apiData.dataRefreshRate,
      },
      () => {
        this.handleEnvironmentChange(this.state.selectedEnvironment);
      }
    );
  };

  modifySchema = (schema) => {
    schema.paths = addEndpointToPaths(schema.paths, endpointReplacements);
    for (const [, operations] of Object.entries(schema.paths)) {
      for (const [, operation] of Object.entries(operations)) {
        operation.parameters = replaceParameters(
          operation.parameters,
          parameterReplacements
        );
      }
    }

    return schema;
  };

  generateEndpointList = (schema) => {
    const endpointList = [];
    for (const [path, operations] of Object.entries(schema.paths)) {
      for (const [verb, operation] of Object.entries(operations)) {
        endpointList.push({
          method: verb,
          name: operation.operationId || path,
          path: path,
          schema: operation,
          key: verb + "-" + path,
        });
      }
    }
    return endpointList;
  };

  handleEnvironmentChange = (env) => {
    const versionList = this.state.environments[env]?.instances.map(
      (inst) => inst.version
    );

    versionList &&
      this.setState(
        {
          endpoint: null,
          selectedEnvironment: env,
          versions: versionList,
          selectedVersion: versionList[0],
        },
        () => {
          this.handleVersionChange(this.state.versions[0]);
        }
      );
  };

  async handleVersionChange(version, selectedIndex = 0) {
    const instances = this.state.environments[this.state.selectedEnvironment]
      .instances;
    const instance = this.state.environments[
      this.state.selectedEnvironment
    ].instances.find(
      (inst) =>
        inst.version === version && instances.indexOf(inst) === selectedIndex
    );
    const specSelectedVersion = instance.spec;
    const proxyUrlSelectedVersion = instance.config.proxyUrl;
    const lifeCycleStatusSelectedVersion = instance.lifecycleStatus;
    const instanceNamespace = instance.config.namespace;
    const advancedProxySettingsSelectedVersion = instance.config.advancedProxySettings;
    const specContent = await prepareSpec(specSelectedVersion);

    this.setState({
      endpoint: null,
      selectedVersion: version,
      content: specContent,
      proxyUrl: proxyUrlSelectedVersion,
      lifeCycleStatus: lifeCycleStatusSelectedVersion,
      namespace: instanceNamespace,
      advancedProxySettings: advancedProxySettingsSelectedVersion
    });
  }

  handleAttributeChange = (attributeKey, option) => {
    if (attributeKey === "env") {
      this.handleEnvironmentChange(option.label);
    } else if (attributeKey === "ver") {
      const version = option.label;
      const selIndex = option.value;
      this.handleVersionChange(version, selIndex);
    }
  };

  formatDataRefresh = (rate) => {
    const dataRefreshRegex = /(\d+)([a-z])/;
    const rateMeasurementMap = {
      m: 'min',
      h: 'hour',
      d: 'day'
    };
    const dataRefreshMatches = rate.match(dataRefreshRegex);
    const dataRefreshRate = dataRefreshMatches[1];
    const rateMeasurement = dataRefreshMatches[2];
    const formattedRateMeasurement = dataRefreshRate > 1 && rateMeasurement !== 'm'
      ? `${rateMeasurementMap[rateMeasurement]}s`
      : rateMeasurementMap[rateMeasurement];
    return `${dataRefreshRate} ${rateMeasurementMap[rateMeasurement] ? formattedRateMeasurement : ''}`
  }

  formatRateLimit = (rate) => {
    const rateLimitRegex = /(\d+)(p[a-z])/;
    const rateMeasurementMap = {
      ps: 'sec',
      pm: 'min'
    };
    const ratelimitMatches = rate.match(rateLimitRegex);
    const rateLimit = ratelimitMatches[1];
    const rateMeasurement = ratelimitMatches[2];
    const formattedRateMeasurement = rateLimit > 1
      ? `calls/${rateMeasurementMap[rateMeasurement]}`
      : `call/${rateMeasurementMap[rateMeasurement]}`;
    return `${rateLimit} ${rateMeasurementMap[rateMeasurement] ? formattedRateMeasurement : ''}`
  }

  render() {
    const {
      state: { endpoint, content, proxyUrl, lifeCycleStatus },
    } = this;
    let schema = content ? this.modifySchema(content) : content;
    let endpoints = schema ? this.generateEndpointList(schema) : [];

    const { auth = {}, downloadSpec } = this.props;
    const { isAuthenticated = false } = auth;
    const customLifeCycleStatus = !Object.values(API_VERSION_STATUS).includes(
      lifeCycleStatus
    );
    const environmentList = Object.keys(this.state.environments).filter(
      (env) => env !== "dev"
    );

    return (
      <div className="api-dir-pkg_api-tab-container api-dir-pkg_generic-style-for-browsers">
        <ApiHeaderDetails className="header-section">
          <div className="header-content">
            <div>
              <APIDetailsHeader
                title={
                  this.props.bundleHeader
                    ? this.props.api?.name
                    : "API Specifications"
                }
              />
              {isAuthenticated && (
                <Fragment>
                  {proxyUrl && (
                    <section className="api-dir-pkg_proxyUrl">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <span>{`Proxy URL:`}</span>
                        <span className="api-dir-pkg_spec-proxy">
                          {this.state.proxyUrl}
                        </span>
                        <CopyToClipboard
                          textToCopy={this.state.proxyUrl}
                          size={24}
                          fill={"rgba(21, 27, 37, 0.7)"}
                        />
                      </div>
                    </section>
                  )}
                </Fragment>
              )}
            </div>
            {(isAuthenticated && lifeCycleStatus) && (
              <div>
                {(lifeCycleStatus || customLifeCycleStatus) && (
                  <ReleaseTag status={lifeCycleStatus}>
                    {API_VERSION_STATUS.GENERAL_RELEASE}
                  </ReleaseTag>
                )}
              </div>
            )}
          </div>
        </ApiHeaderDetails>
        <Fragment>
          {!isAuthenticated && (
            <ApiVersionSection className="api-dir-pkg_version-details">
              {this.state.selectedVersion && (
                <div className="api-dir-pkg_version-id">
                  Version: {this.state.selectedVersion}
                </div>
              )}
              <div className="api-dir-pkg_api-release-download-container">
                <SpecDownload
                  downloadSpec={downloadSpec}
                  content={content}
                />
              </div>
            </ApiVersionSection>
          )}
          {isAuthenticated && (
            <ApiVersionSection className="api-dir-pkg_version-details-auth">
              {(environmentList.length > 0 ||
              this.state.versions.length > 0) && (
                <Fragment>
                  {Object.keys(this.state.environments).length > 0 && (
                    <AttributeSelection
                      dataTestId="environment-select"
                      key="environment-drop-down"
                      id="environment-drop-down"
                      attributeKey="env"
                      value={this.state.selectedEnvironment}
                      items={environmentList}
                      onItemSelection={this.handleAttributeChange}
                    />
                  )}
                  {this.state.versions.length > 0 && (
                    <AttributeSelection
                      dataTestId="version-select"
                      key="version-drop-down"
                      id="version-drop-down"
                      attributeKey="ver"
                      value={this.state.selectedVersion}
                      items={this.state.versions}
                      onItemSelection={this.handleAttributeChange}
                    />
                  )}
                </Fragment>
              )}
              {(this.state.dataRefreshRate ||
                this.state.advancedProxySettings?.rateLimit ||
                this.state.advancedProxySettings?.rateLimitPerApp) &&
                  <VersionSeperator />
              }
              {this.state.dataRefreshRate &&
                <RateInput
                  dataTestId="data-refresh-rate-input"
                  label="Data Refresh Rate"
                  name="data-refresh-rate"
                  value={this.formatDataRefresh(this.state.dataRefreshRate)}
                  isDisabled={true}
                />
              }
              {this.state.advancedProxySettings?.rateLimit &&
                <RateInput
                  dataTestId="overall-rate-limit-input"
                  label="Overall Rate Limit"
                  name="overall-rate-limit"
                  value={this.formatRateLimit(this.state.advancedProxySettings?.rateLimit)}
                  isDisabled={true}
                />
              }
              {this.state.advancedProxySettings?.rateLimitPerApp &&
                <RateInput
                  dataTestId="app-rate-limit-input"
                  label="App Rate Limit"
                  name="app-rate-limit"
                  value={this.formatRateLimit(this.state.advancedProxySettings?.rateLimitPerApp)}
                  isDisabled={true}
                />
              }
              {(this.state.dataRefreshRate ||
                this.state.advancedProxySettings?.rateLimit ||
                this.state.advancedProxySettings?.rateLimitPerApp) &&
                  <VersionSeperator />
              }
              <div className="api-dir-pkg_api-release-download-container">
                <SpecDownload
                  downloadSpec={downloadSpec}
                  content={content}
                />
              </div>
            </ApiVersionSection>
          )}
        </Fragment>
        {((!this.props.oneToOneApiAnon && this.state.versions.length === 0) ||
          (this.props.oneToOneApiAnon &&
            (!endpoints || endpoints.length === 0))) &&
          noVersionsText(this.props.auth)}
        <EndpointDetails>
          <div className="api-dir-pkg_endpoints-list-wrapper">
            <EndpointsList>
              <Endpoints className="endpoints-list">
                {endpoints.map((item) => {
                  return (
                    <EndpointListItem key={item.key} id={item.key}>
                      <EndpointListInfo
                        onClick={() => this.setEndpoint(item)}
                        method={item.method}
                        data-cy={`apiexplorer-endpoint-${item.key}`}
                        isSelected={
                          endpoint
                            ? item.key === endpoint.key
                            : item.key === endpoints[0].key
                        }
                      >
                        <EndpointListMethod method={item.method}>
                          {item.method}
                        </EndpointListMethod>
                        <EndpointListName method={item.method} title={cleanName(item.name)}>
                          {cleanName(item.name)}
                        </EndpointListName>
                      </EndpointListInfo>
                    </EndpointListItem>
                  );
                })}
              </Endpoints>
            </EndpointsList>
            {content && (
              <div>
                <ApiEndpoint
                  schema={content}
                  endpoint={endpoint || endpoints[0]}
                  environment={
                    this.state.selectedEnvironment === "Test"
                      ? API_ENVIRONMENTS.Test
                      : API_ENVIRONMENTS.Production
                  }
                  namespace={this.state.namespace}
                  proxyUrl={this.state.proxyUrl}
                />
              </div>
            )}
          </div>
          {!!endpoints && endpoints.length > 0 && (
            <div className="api-dir-pkg_try-it-out-holder">
              <TestSimulator
                schema={content}
                endpoint={endpoint || endpoints[0]}
                environmentURLs={this.props.environmentURLs}
              />
            </div>
          )}
        </EndpointDetails>
      </div>
    );
  }
}

export default ApiExplorer;
